if (!customElements.get('space-inspiration')) {
  class SpaceInspiration extends HTMLElement {
    constructor() {
      super();
      this.frameRequested = false;
      this.isDragging = false;
      this.didDrag = false;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-space-track]');
      this.previousButton = this.querySelector('[data-space-previous]');
      this.nextButton = this.querySelector('[data-space-next]');
      this.progressBar = this.querySelector('[data-space-progress]');

      if (!this.track || !this.previousButton || !this.nextButton || !this.progressBar) return;

      this.handleScroll = this.requestUpdate.bind(this);
      this.handleResize = this.requestUpdate.bind(this);
      this.handlePointerDown = this.onPointerDown.bind(this);
      this.handlePointerMove = this.onPointerMove.bind(this);
      this.handlePointerUp = this.onPointerUp.bind(this);
      this.handleClick = this.onClick.bind(this);
      this.handleKeydown = this.onKeydown.bind(this);

      this.previousButton.addEventListener('click', () => this.move(-1));
      this.nextButton.addEventListener('click', () => this.move(1));
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.handlePointerDown);
      this.track.addEventListener('pointermove', this.handlePointerMove);
      this.track.addEventListener('pointerup', this.handlePointerUp);
      this.track.addEventListener('pointercancel', this.handlePointerUp);
      this.track.addEventListener('click', this.handleClick, true);
      this.track.addEventListener('keydown', this.handleKeydown);
      window.addEventListener('resize', this.handleResize, { passive: true });

      this.update();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.handleScroll);
      this.track.removeEventListener('pointerdown', this.handlePointerDown);
      this.track.removeEventListener('pointermove', this.handlePointerMove);
      this.track.removeEventListener('pointerup', this.handlePointerUp);
      this.track.removeEventListener('pointercancel', this.handlePointerUp);
      this.track.removeEventListener('click', this.handleClick, true);
      this.track.removeEventListener('keydown', this.handleKeydown);
      window.removeEventListener('resize', this.handleResize);
    }

    requestUpdate() {
      if (this.frameRequested) return;
      this.frameRequested = true;
      requestAnimationFrame(() => {
        this.update();
        this.frameRequested = false;
      });
    }

    update() {
      const maximumScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      const scrollPosition = Math.min(Math.max(this.track.scrollLeft, 0), maximumScroll);
      const visibleRatio = Math.min(this.track.clientWidth / this.track.scrollWidth, 1);
      const progressWidth = visibleRatio * 100;
      const progressLeft = maximumScroll > 0 ? (scrollPosition / maximumScroll) * (100 - progressWidth) : 0;

      this.progressBar.style.width = `${progressWidth}%`;
      this.progressBar.style.left = `${progressLeft}%`;
      this.previousButton.disabled = scrollPosition <= 1;
      this.nextButton.disabled = scrollPosition >= maximumScroll - 1;
    }

    move(direction) {
      const cards = Array.from(this.track.querySelectorAll('[data-space-card]'));
      const currentPosition = this.track.scrollLeft;
      let target;

      if (direction > 0) {
        target = cards.find((card) => card.offsetLeft > currentPosition + 8);
      } else {
        target = cards.reverse().find((card) => card.offsetLeft < currentPosition - 8);
      }

      this.track.scrollTo({
        left: target ? target.offsetLeft : direction > 0 ? this.track.scrollWidth : 0,
        behavior: 'smooth',
      });
    }

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this.isDragging = true;
      this.didDrag = false;
      this.startX = event.clientX;
      this.startScroll = this.track.scrollLeft;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.isDragging) return;
      const distance = event.clientX - this.startX;
      if (Math.abs(distance) > 5) this.didDrag = true;
      this.track.scrollLeft = this.startScroll - distance;
    }

    onPointerUp(event) {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.track.classList.remove('is-dragging');
      if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      window.setTimeout(() => {
        this.didDrag = false;
      }, 0);
    }

    onClick(event) {
      if (!this.didDrag) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.move(event.key === 'ArrowRight' ? 1 : -1);
    }
  }

  customElements.define('space-inspiration', SpaceInspiration);
}
